// v0.5.2 / v0.6 — extracted from CalculatePanel.jsx (LOC-cap sweep).
// Pure derivations over the review queue's MISSING_EXCHANGE_RATE groups.
// No JSX, no React import needed — kept out of CalculatePanel.jsx so
// that file stays under its 500-line cap. Deliberately no imports from
// PeriodFxRates.jsx or any other UI component: that pulls in a
// Jest-unresolvable "@/lib/utils" alias transitively (InfoHint ->
// popover), breaking this file's own unit tests.

/** Returns the list of source currencies (for ONE target currency)
 *  still missing a numeric rate value. Caller uses this to gate the
 *  Run Calc button — call once per target currency the calc actually
 *  needs (see engine.calc_row_rules.apply_row_fx_flag's docstring for
 *  why a UK_VAT calc can need both GBP and EUR).
 *  `ratesForTarget` is `periodFxRates[targetCurrency] || {}` — the
 *  slice for THIS target, not the whole nested map. `extraCurrencies`
 *  covers rows whose need this target doesn't show up in
 *  `selectedRuns`' own declared currency (e.g. a per-row currency
 *  inside a base-currency file, or a currency only the classification-
 *  derived preview/review-queue pairs know needs THIS specific target). */
export const missingFxRates = (
  selectedRuns, targetCurrency, ratesForTarget, extraCurrencies = [],
) => {
  const missing = [];
  const seen = new Set();
  const check = (c) => {
    if (!c || c === targetCurrency || seen.has(c)) return;
    seen.add(c);
    const v = ratesForTarget?.[c];
    if (v === undefined || v === null || String(v).trim() === "") {
      missing.push(c);
      return;
    }
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) missing.push(c);
  };
  for (const r of selectedRuns || []) {
    check((r?.declared_currency || "").toUpperCase());
  }
  for (const c of extraCurrencies || []) {
    check((c || "").toUpperCase());
  }
  return missing;
};

// v0.6 — was deriveFlaggedFxCurrencies (currency-only). PeriodFxRates now
// needs the TARGET too (a calc can need both a GBP box and a EUR box —
// see engine.calc_row_rules.apply_row_fx_flag's docstring), so this
// reads the same group.fx_pairs the monthly-grid derivation below
// already does, filtered to the "oss" section (the flat OSS/UK-legacy
// path — "national" is deriveFlaggedMonthlyFxPairs's own domain, see
// MissingRateAction.jsx's national-vs-oss split). Feeds PeriodFxRates
// as [source, target] pairs, same shape deriveFlaggedMonthlyFxPairs
// already returns.
export const deriveFlaggedFxPairs = (groups) => {
  const seen = new Set();
  const out = [];
  for (const g of groups || []) {
    if (g.flag_type !== "MISSING_EXCHANGE_RATE") continue;
    for (const p of g.fx_pairs || []) {
      if (p.section !== "oss") continue;
      const [src, tgt] = String(p.pair || "").split("→");
      if (!src || !tgt) continue;
      const key = `${src}|${tgt}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([src, tgt]);
    }
  }
  return out;
};

// v0.6 — same fix as above, for the per-country monthly grid. That grid
// previously derived its rows ONLY from the operator's own local
// registrations, so an OSS destination country (by definition, one you
// are NOT locally registered in) could never get a box even when the
// review queue flags it as missing — a structural dead end for the
// single most common OSS case. Source of truth is the SAME
// group.fx_pairs the "Needs EUR→PLN" label already reads, filtered to
// the national-grid section (OSS/UK-flat pairs are handled by
// deriveFlaggedFxCurrencies/PeriodFxRates instead — see
// MissingRateAction.jsx's own national-vs-oss split). Feeds
// MonthlyFxRates as extraPairs.
export const deriveFlaggedMonthlyFxPairs = (groups) => {
  const seen = new Set();
  const out = [];
  for (const g of groups || []) {
    if (g.flag_type !== "MISSING_EXCHANGE_RATE") continue;
    for (const p of g.fx_pairs || []) {
      if (p.section !== "national") continue;
      const [src, tgt] = String(p.pair || "").split("→");
      if (!src || !tgt) continue;
      const key = `${src}|${tgt}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([src, tgt]);
    }
  }
  return out;
};

// v0.6 Stage 2 — same two derivations as above, but from the NEW
// pre-calc POST /api/calculate/fx-preview response instead of a past
// calc's review queue. Response shape: {fx_requirements: [{currency,
// filing_currency, month, is_national}], ...}. is_national splits the
// same way the two UI sections above already split: false → the flat
// OSS/UK aggregate rate (PeriodFxRates), true → the per-country
// monthly grid (MonthlyFxRates). Deliberately pair-only (no month) —
// both target components already re-derive which months they need
// from the selected period; the preview's job is only to say WHICH
// currency/target pairs need a box at all.
// v0.6 (period_fx_rates two-target fix) — was derivePreviewFxCurrencies
// (currency-only, discarded filing_currency). The backend has always
// computed filing_currency correctly per row; this just stops throwing
// it away, so PeriodFxRates can tell a GBP-target need from a
// EUR-target one instead of assuming a single calc-wide base.
export const derivePreviewFxPairs = (fxRequirements) => {
  const seen = new Set();
  const out = [];
  for (const r of fxRequirements || []) {
    if (r.is_national) continue;
    const src = String(r.currency || "").toUpperCase();
    const tgt = String(r.filing_currency || "").toUpperCase();
    if (!src || !tgt) continue;
    const key = `${src}|${tgt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([src, tgt]);
  }
  return out;
};

// v0.6 (period_fx_rates two-target fix) — [source, target] pairs (from
// deriveFlaggedFxPairs/derivePreviewFxPairs, already merged) split into
// per-target source-currency lists. Shared by FxRatesSection (which
// target(s) to render a box for) and CalculatePanel (the Run-button
// gate, which must check both targets a UK_VAT calc can need) so the
// split rule lives in exactly one place.
export const splitPairsByTarget = (pairs) => ({
  gbp: (pairs || []).filter(([, tgt]) => tgt === "GBP").map(([src]) => src),
  eur: (pairs || []).filter(([, tgt]) => tgt === "EUR").map(([src]) => src),
});

// v0.6 (period_fx_rates two-target fix) — the Run-button gate. A
// UK_VAT calc can need a rate for BOTH targets at once (GBP for
// GB-attributed rows, EUR for genuine cross-border OSS rows — see
// engine.calc_row_rules.apply_row_fx_flag's docstring), so this checks
// both, not just the regime's own base. An EU_OSS calc never has a
// second target (its base already is EUR), so the second check only
// ever fires for a UK_VAT calc with a real cross-border-to-EUR need.
export const computeFxMissing = (selectedRuns, regime, periodFxRates, mergedFlaggedFxPairs) => {
  const regimeBase = regime === "UK_VAT" ? "GBP" : "EUR";
  const { gbp: gbpExtra, eur: eurExtra } = splitPairsByTarget(mergedFlaggedFxPairs);
  const regimeExtra = regimeBase === "GBP" ? gbpExtra : eurExtra;
  const otherTarget = regimeBase === "GBP" ? "EUR" : null;
  return [
    ...missingFxRates(selectedRuns, regimeBase, periodFxRates[regimeBase] || {}, regimeExtra),
    ...(otherTarget && eurExtra.length > 0
      ? missingFxRates([], otherTarget, periodFxRates[otherTarget] || {}, eurExtra)
      : []),
  ];
};

// v0.6.10 — the Run-button gate above (computeFxMissing) only ever
// checked the flat OSS/UK period box; a TYPED (not blank — blank is
// fine, handled by the post-calc MISSING_EXCHANGE_RATE review flow)
// invalid value in the per-country MONTHLY grid — zero, negative, or
// non-numeric — never blocked Calculate at all. The operator only
// found out via a generic 422 from the backend's own
// `rate <= 0` rejection (engine/calc_request.py), with no indication
// of which cell was wrong. Mirrors that same rule client-side: a
// filled cell must be a finite number > 0, exactly like a period-box
// entry already has to be.
export const monthlyFxInvalidCells = (monthlyFxRates) => {
  const out = [];
  for (const [src, byTarget] of Object.entries(monthlyFxRates || {})) {
    for (const [tgt, byMonth] of Object.entries(byTarget || {})) {
      for (const [month, cell] of Object.entries(byMonth || {})) {
        const raw = cell?.rate;
        if (raw === undefined || raw === null || String(raw).trim() === "") {
          continue; // blank — not this gate's concern.
        }
        const n = Number(raw);
        if (!Number.isFinite(n) || n <= 0) {
          out.push({ src, tgt, month });
        } else if (n === 1 && !String(cell?.source || "").trim()) {
          // A1-93 r4 — mirrors engine/calc_request.py: a rate of exactly 1
          // with no source cited is refused there (a placeholder, not a
          // published rate). Said here instead of as an unexplained error.
          out.push({ src, tgt, month, needsSource: true });
        }
      }
    }
  }
  return out;
};

// Shared by CalculatePanel's blockedReason label and its run() guard
// so the two can't drift into different wording for the same gate.
export const formatFxInvalidPairs = (fxInvalid) =>
  fxInvalid.map((c) => `${c.src}→${c.tgt} (${c.month})`).join(", ");
// The supersede dialog (workflow/supersedePlan) has no translator: the same
// two sentences as fxInvalidText, in English.
export const formatFxInvalidMessage = (fxInvalid) => fxInvalidText((_k, o) => o.defaultValue, fxInvalid);

// A2-517 — the two refusals say what each one is. A rate of exactly 1 is a
// real number refused only for want of a source; "enter a valid rate (greater
// than zero)" was shown for it, and the operator re-typed the 1 three times.
export const fxInvalidText = (t, fxInvalid) => {
  const bad = fxInvalid.filter((c) => !c.needsSource);
  const one = fxInvalid.filter((c) => c.needsSource);
  return [
    bad.length ? t("ui.calculate.blocked_fx_rate_invalid", { pairs: formatFxInvalidPairs(bad),
      defaultValue: `Enter a valid rate (greater than zero) for ${formatFxInvalidPairs(bad)}.` }) : "",
    one.length ? t("ui.calculate.blocked_fx_rate_needs_source", { pairs: formatFxInvalidPairs(one),
      defaultValue: `A rate of exactly 1 needs its source: in the box under it, say where the rate comes from (${formatFxInvalidPairs(one)}).` }) : "",
  ].filter(Boolean).join(" ");
};

export const derivePreviewMonthlyFxPairs = (fxRequirements) => {
  const seen = new Set();
  const out = [];
  for (const r of fxRequirements || []) {
    if (!r.is_national) continue;
    const src = String(r.currency || "").toUpperCase();
    const tgt = String(r.filing_currency || "").toUpperCase();
    if (!src || !tgt) continue;
    const key = `${src}|${tgt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([src, tgt]);
  }
  return out;
};
